import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { backendErrorMessage, logWebSocketError, readJsonResponse } from "../apiDiagnostics";
import { API_URL, WS_URL } from "../config";
import PageShell from "../components/PageShell";

const STUDENT_SESSION_PREFIX = "klasseordnung-student-session:";

function normalizeName(name) {
  return name.trim().toLowerCase();
}

function getStudentSessionKey(listId) {
  return `${STUDENT_SESSION_PREFIX}${listId}`;
}

function readStudentSession(listId) {
  try {
    const storedSession = window.sessionStorage.getItem(getStudentSessionKey(listId));
    if (!storedSession) return { studentName: "", assigneeIds: [] };

    const parsedSession = JSON.parse(storedSession);
    return {
      studentName: typeof parsedSession.studentName === "string" ? parsedSession.studentName : "",
      assigneeIds: Array.isArray(parsedSession.assigneeIds)
        ? parsedSession.assigneeIds.map(Number).filter(Number.isFinite)
        : []
    };
  } catch (err) {
    return { studentName: "", assigneeIds: [] };
  }
}

function writeStudentSession(listId, sessionData) {
  try {
    window.sessionStorage.setItem(getStudentSessionKey(listId), JSON.stringify(sessionData));
  } catch (err) {
    // Session storage is optional.
  }
}

function getAssignmentsForName(listData, studentName) {
  const cleanStudentName = normalizeName(studentName);
  if (!cleanStudentName) return [];

  return listData.items.flatMap(item =>
    item.assignees
      .filter(person => normalizeName(person.name) === cleanStudentName)
      .map(person => ({ ...person, itemId: item.id, itemName: item.name }))
  );
}

function StudentPage() {
  const { id } = useParams();
  const initialStudentSession = readStudentSession(id);
  const [list, setList] = useState(null);
  const [studentSession, setStudentSession] = useState(initialStudentSession);
  const [studentName, setStudentName] = useState(initialStudentSession.studentName);
  const [error, setError] = useState("");

  function saveStudentSession(nextSession) {
    const cleanSession = {
      studentName: nextSession.studentName || "",
      assigneeIds: Array.from(new Set((nextSession.assigneeIds || []).map(Number).filter(Number.isFinite)))
    };

    setStudentSession(cleanSession);
    writeStudentSession(id, cleanSession);
  }

  async function loadList() {
    const url = `${API_URL}/api/lists/${id}`;
    try {
      const response = await fetch(url);
      const data = await readJsonResponse(response, "Liste laden", url);
      if (response.ok) setList(data);
      else setError(data.error || "Liste nicht gefunden.");
    } catch (err) {
      setError(backendErrorMessage("Liste laden", url, err));
    }
  }

  useEffect(() => {
    const storedSession = readStudentSession(id);
    setStudentSession(storedSession);
    setStudentName(storedSession.studentName);
    loadList();

    const wsUrl = `${WS_URL}?listId=${id}`;
    const ws = new WebSocket(wsUrl);
    ws.onerror = event => logWebSocketError(wsUrl, event);
    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "LIST_UPDATED") setList(data.list);
      } catch (err) {
        logWebSocketError(wsUrl, err);
      }
    };

    return () => ws.close();
  }, [id]);

  async function assign(itemId) {
    setError("");
    const cleanStudentName = studentName.trim();
    if (!cleanStudentName) return setError("Bitte gib deinen Namen ein.");

    const knownAssignmentIds = new Set(
      list.items.flatMap(item => item.assignees.map(person => Number(person.id)))
    );

    const url = `${API_URL}/api/lists/${id}/assign`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId, studentName: cleanStudentName, quantity: 1 })
      });
      const data = await readJsonResponse(response, "Eintragen", url);
      if (!response.ok) return setError(data.error || "Eintrag nicht möglich.");

      const newOwnAssignments = getAssignmentsForName(data, cleanStudentName)
        .filter(person => Number(person.itemId) === Number(itemId) && !knownAssignmentIds.has(Number(person.id)));
      const fallbackOwnAssignments = newOwnAssignments.length > 0
        ? newOwnAssignments
        : getAssignmentsForName(data, cleanStudentName);

      saveStudentSession({
        studentName: cleanStudentName,
        assigneeIds: [
          ...studentSession.assigneeIds,
          ...fallbackOwnAssignments.map(person => person.id)
        ]
      });
      setStudentName(cleanStudentName);
      setList(data);
    } catch (err) {
      setError(backendErrorMessage("Eintragen", url, err));
    }
  }

  async function unassign(assigneeId) {
    setError("");
    const cleanStudentName = studentName.trim() || studentSession.studentName.trim();
    if (!cleanStudentName) return setError("Bitte gib deinen Namen ein.");

    const url = `${API_URL}/api/lists/${id}/unassign`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assigneeId, studentName: cleanStudentName })
      });
      const data = await readJsonResponse(response, "Austragen", url);
      if (!response.ok) return setError(data.error || "Austragen nicht möglich.");
      saveStudentSession({
        studentName: cleanStudentName,
        assigneeIds: studentSession.assigneeIds.filter(storedId => Number(storedId) !== Number(assigneeId))
      });
      setStudentName(cleanStudentName);
      setList(data);
    } catch (err) {
      setError(backendErrorMessage("Austragen", url, err));
    }
  }

  if (!list) {
    return (
      <PageShell maxWidth="max-w-3xl">
        <div className="rounded-2xl border border-[#dce8c8] bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold">Ordnungsliste wird geladen</h1>
          {error && <p className="mt-3 text-sm font-medium text-red-700">{error}</p>}
        </div>
      </PageShell>
    );
  }

  const storedAssigneeIds = new Set(studentSession.assigneeIds.map(Number));
  const isOwnAssignee = person => storedAssigneeIds.has(Number(person.id));
  const currentStudentAssignments = list.items.flatMap(item =>
    item.assignees
      .filter(isOwnAssignee)
      .map(person => ({ ...person, itemName: item.name }))
  );
  const hasCurrentStudentAssignment = currentStudentAssignments.length > 0;

  return (
    <PageShell maxWidth="max-w-3xl">
      <header className="mb-8">
        <p className="mb-3 text-xs font-medium uppercase tracking-[0.22em] text-neutral-500">KlasseOrdnung</p>
        <h1 className="text-4xl font-semibold tracking-[-0.04em]">{list.title}</h1>
        <p className="mt-3 text-base leading-7 text-neutral-600">
          Trag bitte deinen Namen ein und wähle deinen Dienst oder deine Aufgabe aus.
        </p>

        <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-base font-bold text-amber-900">
          {list.singleChoice
            ? "Wichtig: Du kannst dich nur für einen Eintrag anmelden und belegst dort 1 Platz."
            : "Wichtig: Deine Lehrkraft erlaubt mehrere Einträge. Jeder Eintrag belegt 1 Platz."}
        </div>
      </header>

      {list.closed && (
        <div className="mb-5 rounded-xl border border-[#efd26b] bg-[#fff8d8] px-4 py-3 text-sm font-medium text-[#735b08]">
          Diese Ordnungsliste ist geschlossen.
        </div>
      )}

      <section className="mb-5 rounded-2xl border border-[#dce8c8] bg-white p-5 shadow-sm">
        <label className="mb-2 block text-sm font-medium text-neutral-700">Dein Name</label>
        <input
          className="w-full rounded-xl border-[#c9d9b4] px-4 py-3 text-base focus:border-[#6d8f36] focus:ring-[#6d8f36]"
          placeholder="z.B. Max"
          value={studentName}
          disabled={list.closed}
          onChange={e => setStudentName(e.target.value)}
        />
        {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</div>}
      </section>

      <section className="space-y-3">
        {list.items.map(item => {
          const usedAmount = item.assignees.reduce((sum, person) => sum + Number(person.quantity || 1), 0);
          const remainingAmount = item.max_count - usedAmount;
          const full = remainingAmount <= 0;
          const ownAssignees = item.assignees.filter(isOwnAssignee);
          const hasOwnEntryForItem = ownAssignees.length > 0;
          const canAssign = !full && !list.closed && !hasOwnEntryForItem && (!list.singleChoice || !hasCurrentStudentAssignment);

          return (
            <article key={item.id} className="rounded-2xl border border-[#dce8c8] bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold tracking-[-0.02em]">{item.name}</h2>
                  <p className="mt-1 text-sm text-neutral-500">{usedAmount} von {item.max_count} Plätzen vergeben</p>
                </div>
                <span className={full ? "rounded-full bg-[#172017] px-3 py-1 text-xs font-semibold text-white" : "rounded-full bg-[#eef5df] px-3 py-1 text-xs font-semibold text-[#39501f]"}>
                  {full ? "Voll" : "Offen"}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {item.assignees.length === 0 ? (
                  <p className="text-sm text-neutral-500">Noch niemand eingetragen.</p>
                ) : (
                  item.assignees.map(person => {
                    const isOwnEntry = isOwnAssignee(person);

                    return (
                      <span key={person.id} className="inline-flex items-center gap-2 rounded-full bg-[#f8faf2] px-3 py-1.5 text-sm text-neutral-800">
                        {person.name}
                        {isOwnEntry && !list.closed && (
                          <button
                            type="button"
                            onClick={() => unassign(person.id)}
                            className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                          >
                            Austragen
                          </button>
                        )}
                      </span>
                    );
                  })
                )}
              </div>

              {hasOwnEntryForItem && !list.closed && (
                <p className="mt-4 text-sm font-medium text-[#39501f]">Du bist hier eingetragen.</p>
              )}

              {list.singleChoice && !hasOwnEntryForItem && hasCurrentStudentAssignment && !list.closed && (
                <p className="mt-4 text-sm text-neutral-500">
                  Du bist bereits bei {currentStudentAssignments[0].itemName} eingetragen.
                </p>
              )}

              {canAssign && (
                <div className="mt-5">
                  <button onClick={() => assign(item.id)} className="w-full rounded-xl bg-[#172017] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#263826] sm:w-auto">
                    Eintragen
                  </button>
                </div>
              )}
            </article>
          );
        })}
      </section>
    </PageShell>
  );
}

export default StudentPage;
